import { CircleAlert, MessageCircle, X } from 'lucide-react'
import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import { REGIONS_BY_ID } from '@snootsvet/shared/portal'

import { AppointmentChangeWindowClosedAlert } from '@/customer/components/booking/appointment-change-window-closed-alert'
import { BookingContextCard } from '@/customer/components/booking/booking-context-card'
import { LoadingSpinner } from '@/customer/components/loading-spinner'
import { PublicPage } from '@/customer/components/public-page'
import { Alert, AlertTitle } from '@/customer/components/ui/alert'
import { Button } from '@/customer/components/ui/button'
import { useDocumentTitle } from '@/customer/hooks/use-document-title'
import { usePublicBookingContext } from '@/customer/hooks/use-public-booking-context'
import { usePublicCancelAppointment } from '@/customer/hooks/use-public-cancel-appointment'
import { captureEvent } from '@/customer/lib/posthog'

/**
 * Public appointment-cancel page. Reached from the cancel link in
 * confirmation / reminder emails:
 *
 *   /booking/cancel?token=<signed>
 *
 * Mirrors the confirm-cancel view of `ManageAppointmentDialog` but as
 * a standalone page authed by the URL token. Uses the same primitives
 * (`BookingContextCard`, destructive `Button`) so the visual language
 * matches the portal surface.
 */
export function PublicCancellationPage() {
  useDocumentTitle('Cancel appointment')
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token') || null

  const contextQuery = usePublicBookingContext(token)
  const cancelMutation = usePublicCancelAppointment(token)
  const [done, setDone] = useState(false)

  if (done) {
    return (
      <PublicPage
        headline={[
          { text: 'Appointment', color: 'yellow' },
          { text: 'cancelled', color: 'green' },
        ]}
      >
        <p className="text-body-lg text-muted-foreground">
          We've let the clinic know. You can always book another visit when you're ready.
        </p>
      </PublicPage>
    )
  }

  if (!token) {
    return <CancelMessage message="This cancellation link is invalid or has expired" />
  }

  if (contextQuery.isError) {
    return <CancelMessage message={contextQuery.error.message} />
  }

  if (!contextQuery.data) {
    return (
      <PublicPage>
        <LoadingSpinner />
      </PublicPage>
    )
  }

  const context = contextQuery.data

  if (!context.currentAppointment) {
    return <CancelMessage message="No appointment found for this link" />
  }

  const isPastCancelDeadline = new Date() >= new Date(context.currentAppointment.cancelDeadline)

  if (isPastCancelDeadline) {
    return (
      <PublicPage
        headline={[
          { text: 'Cancellation', color: 'yellow' },
          { text: 'no longer', color: 'destructive' },
          { text: 'available', color: 'blue' },
        ]}
      >
        <AppointmentChangeWindowClosedAlert clinicId={context.clinic.id} />
      </PublicPage>
    )
  }

  const handleCancel = () => {
    cancelMutation.mutate(undefined, {
      onSuccess: () => {
        captureEvent('appointment_cancelled', {
          source: 'booking_link',
          appointment_id: context.appointmentId,
          appointment_type_id: context.appointmentType.id,
          clinic_id: context.clinic.id,
        })
        setDone(true)
      },
    })
  }

  const cancelError = cancelMutation.error?.message ?? null

  const start = new Date(context.currentAppointment.startTime)

  return (
    <PublicPage
      headline={[
        { text: 'Cancel', color: 'pink' },
        { text: `${context.patient.name}'s`, color: 'yellow' },
        { text: 'appointment?', color: 'blue' },
      ]}
    >
      <div className="flex flex-col gap-8">
        <BookingContextCard
          appointmentTypeId={context.appointmentType.id}
          clinicId={context.clinic.id}
          start={start}
          region={REGIONS_BY_ID[context.clinic.region]}
          timezone={context.clinic.timezone}
          petName={context.patient.name}
          vetName={context.vetName ?? undefined}
        />

        {cancelError ? (
          <Alert variant="destructive">
            <CircleAlert />
            <AlertTitle>{cancelError}</AlertTitle>
          </Alert>
        ) : null}

        <Button
          type="button"
          variant="destructive"
          className="w-full"
          disabled={cancelMutation.isPending}
          onClick={handleCancel}
        >
          <X data-icon="inline-start" />
          {cancelMutation.isPending ? 'Cancelling…' : 'Yes, cancel appointment'}
        </Button>
      </div>
    </PublicPage>
  )
}

/**
 * Standalone error / status screen for the public cancel flow. Shown
 * when the token is missing / expired or the context fetch fails before
 * we have anything to render.
 */
function CancelMessage({ message }: { message: string }) {
  return (
    <PublicPage
      headline={[
        { text: 'This link', color: 'pink' },
        { text: "doesn't work", color: 'yellow' },
      ]}
    >
      <Alert variant="warning">
        <MessageCircle />
        <AlertTitle>{message}</AlertTitle>
      </Alert>
    </PublicPage>
  )
}
